import type { Observable } from 'rxjs';
import { iif, map, mergeMap, of, retry, tap } from 'rxjs';

import { getDomainMarketOrigin } from '@libs/search-service-smart-search';
import type {
  AccommodationType,
  AirportStaticInfo,
  Coordinate,
  DestinationAutoCompleteSuggestion,
  FlightAutoCompleteSuggestion,
  GetHotelsResponseMap,
  PortStaticInfo,
  SeaportStaticInfo,
  StaticInfoResponse,
  StaticInfoType,
  TransportVehicle,
} from '@libs/trip-planner-interfaces';
import { CategoryType } from '@libs/trip-planner-interfaces';
import {
  assertValue,
  popularityAirportComparer,
  renderQuery,
} from '@libs/utils';
import type { QueryString } from '@libs/utils';
import { trackFallbackStaticInfoReceived } from '@libs/feature-toggle';
import type { HotelConstraint } from '@libs/constraint-builder';

import { i18n } from '../../localization/localizationSetup';
import { ApiQuery } from '../shared/apiQuery';
import { camelCased } from '../shared/apiQuery.helper';
import { getImageUrl } from '../hotels';

import type {
  ContentApiFerryRouteStaticData,
  ContentApiFerrySuggestionRequestData,
  ContentApiPortStaticData,
  DestinationInformationResponse,
} from './contentApiResponses';
import { getHotelsUrlBuilder } from './getHotelsUrlBuilder';
import { GET_HOTELS_ENDPOINT_PROPERTIES } from './hotelStaticProperties';

const { t } = i18n.global;

type AutoSuggestionType =
  | 'Harbour'
  | 'Airport'
  | 'AirportForDestination'
  | 'FerryRoute';

interface BaseAutoSuggestionOptions {
  type: AutoSuggestionType;
  size: number;
}

interface SearchTermAutoSuggestionOptions extends BaseAutoSuggestionOptions {
  searchTerm: string;
}

interface PositionAutoSuggestionOptions extends BaseAutoSuggestionOptions {
  position: Coordinate;
}

interface IataAutoSuggestionOptions extends BaseAutoSuggestionOptions {
  iata: string;
}

interface HarbourAutoSuggestionOptions extends BaseAutoSuggestionOptions {
  harbour: string;
}

interface FerryRouteSuggestionOptions extends BaseAutoSuggestionOptions {
  termFrom?: string;
  termTo?: string;
  latitudeFrom?: number;
  longitudeFrom?: number;
  latitudeTo?: number;
  longitudeTo?: number;
}

type AutoSuggestionOptions =
  | SearchTermAutoSuggestionOptions
  | PositionAutoSuggestionOptions
  | IataAutoSuggestionOptions
  | HarbourAutoSuggestionOptions
  | FerryRouteSuggestionOptions;

export class ContentApi {
  private query: ApiQuery;
  private readonly baseUrl = 'https://content-hc.sembo.com/';

  public constructor() {
    this.query = new ApiQuery(this.baseUrl);
  }

  public getHotels<T extends StaticInfoType>(
    constraint: HotelConstraint,
    hotelTypes: AccommodationType[],
    type: T
  ): Observable<StaticInfoResponse<T>> {
    const includedProperties = GET_HOTELS_ENDPOINT_PROPERTIES[type];
    const url = getHotelsUrlBuilder(constraint, hotelTypes, includedProperties);

    return this.query
      .getObservable<GetHotelsResponseMap[T]>(url, this.contentApiHeaders())
      .pipe(
        camelCased({
          deep: true,
        }),
        mergeMap((response) =>
          iif(
            () => response.hotels.length === 0,
            this.query
              .getObservable<GetHotelsResponseMap[T]>(
                url,
                this.contentApiHeaders({ forceDefaultMarket: true })
              )
              .pipe(
                camelCased({
                  deep: true,
                }),
                tap((response) => {
                  if (response.hotels.length > 0) {
                    trackFallbackStaticInfoReceived(
                      constraint.selection.serializeValue()
                    );
                  }
                })
              ),
            of(response)
          )
        ),
        retry(1),
        map((response) => ({ response, type }))
      );
  }

  public searchSuggestions(
    searchTerm: string
  ): Observable<DestinationAutoCompleteSuggestion[]> {
    const url = `CategorizedAutosuggestion?Term=${searchTerm}`;
    return this.query.getObservable<DestinationAutoCompleteSuggestion[]>(
      url,
      this.contentApiHeaders()
    );
  }

  public searchAirportSuggestions(
    searchTerm: string
  ): Observable<FlightAutoCompleteSuggestion[]> {
    const options: AutoSuggestionOptions = {
      searchTerm,
      type: 'Airport',
      size: 20,
    };

    return this.searchPortStaticData(options).pipe(
      map((data: ContentApiPortStaticData[]): FlightAutoCompleteSuggestion[] =>
        this.mapToAutoCompleteSuggestion(data, t('planner.airport'))
      )
    );
  }

  public searchAirportSuggestionsByPosition(
    position: Coordinate
  ): Observable<FlightAutoCompleteSuggestion[]> {
    const options: AutoSuggestionOptions = {
      position,
      type: 'Airport',
      size: 20,
    };

    return this.searchPortStaticData(options).pipe(
      map(this.getTopPopularAirports),
      map((data) =>
        this.mapToAutoCompleteSuggestion(
          data,
          t('planner.transport.nearby_airports')
        )
      )
    );
  }

  public searchIataStaticInfo(iata: string): Observable<PortStaticInfo> {
    return this.searchPortStaticData({
      iata,
      type: 'Airport',
      size: 1,
    }).pipe(
      map((data: ContentApiPortStaticData[]): PortStaticInfo => {
        const firstDataAirport = data[0]?.airport;
        assertValue(
          firstDataAirport,
          'contentApi.ts > searchIataStaticInfo > firstDataAirport'
        );

        return {
          code: firstDataAirport.code,
          name: firstDataAirport.label,
          position: {
            latitude: firstDataAirport.latitude,
            longitude: firstDataAirport.longitude,
            name: firstDataAirport.city ?? undefined,
          },
        };
      })
    );
  }

  public searchSeaportStaticInfo(
    harbour: string,
    size: number
  ): Observable<SeaportStaticInfo[]> {
    return this.searchPortStaticData({
      harbour,
      type: 'Harbour',
      size,
    }).pipe(
      map((data: ContentApiPortStaticData[]): SeaportStaticInfo[] =>
        data.map(this.mapToSeaportStaticInfo)
      )
    );
  }

  public searchFerryRouteStaticInfo(
    harbourFrom: string,
    harbourTo: string
  ): Observable<ContentApiFerryRouteStaticData | null> {
    const queryString: QueryString = {
      Types: ['FerryRoute'],
      HarbourFrom: [harbourFrom],
      HarbourTo: [harbourTo],
    };

    return this.query
      .getObservable<ContentApiPortStaticData[]>(
        this.getAutosuggestionUrl(queryString),
        this.contentApiHeaders()
      )
      .pipe(
        camelCased({ deep: true }),
        map((response) => response[0]?.ferryRoute || null)
      );
  }

  public searchAirportStaticInfo(
    searchTerm: string,
    size: number
  ): Observable<AirportStaticInfo[]> {
    return this.searchPortStaticData({
      searchTerm,
      type: 'Airport',
      size,
    }).pipe(
      map((data: ContentApiPortStaticData[]): AirportStaticInfo[] =>
        data.map(this.mapToAirportStaticInfo)
      )
    );
  }

  public searchFerryRoutes(
    searchTerm: string,
    size: number
  ): Observable<ContentApiFerryRouteStaticData[]> {
    return this.searchPortStaticData({
      type: 'FerryRoute',
      searchTerm,
      size,
    }).pipe(
      map(
        (data: ContentApiPortStaticData[]): ContentApiFerryRouteStaticData[] =>
          data.map(this.mapToFerryRoutes)
      )
    );
  }

  public getFerryRoutesSuggestions(
    from: ContentApiFerrySuggestionRequestData,
    to: ContentApiFerrySuggestionRequestData
  ): Observable<ContentApiFerryRouteStaticData[]> {
    const MAX_FERRY_ROUTES = 5;

    const options: AutoSuggestionOptions = {
      type: 'FerryRoute',
      size: MAX_FERRY_ROUTES,
      termTo: to.location,
      latitudeFrom: from.lat,
      longitudeFrom: from.long,
      latitudeTo: to.lat,
      longitudeTo: to.long,
    };

    if (from.location !== to.location) {
      options.termFrom = from.location;
    }

    return this.searchPortStaticData(options).pipe(
      map(
        (data: ContentApiPortStaticData[]): ContentApiFerryRouteStaticData[] =>
          data.map(this.mapToFerryRoutes)
      )
    );
  }

  public searchAirportStaticInfoForDestination(
    searchTerm: string,
    size: number
  ): Observable<AirportStaticInfo[]> {
    return this.searchPortStaticData({
      searchTerm,
      type: 'AirportForDestination',
      size,
    }).pipe(
      map((data: ContentApiPortStaticData[]): AirportStaticInfo[] =>
        data.map(this.mapToAirportStaticInfo)
      )
    );
  }

  public searchAirportStaticInfoForPosition(
    position: Coordinate,
    size: number
  ): Observable<AirportStaticInfo[]> {
    return this.searchPortStaticData({ position, type: 'Airport', size }).pipe(
      map((data: ContentApiPortStaticData[]): AirportStaticInfo[] =>
        data.map(this.mapToAirportStaticInfo)
      )
    );
  }

  public getFerryVehicleTypes(): Observable<TransportVehicle[]> {
    const url = '/VehicleTypes';

    return this.query
      .getObservable<TransportVehicle[]>(url, this.contentApiHeaders())
      .pipe(camelCased({ deep: true }));
  }

  public getDestinationInformation(
    polygonId: number
  ): Observable<DestinationInformationResponse> {
    const url = `/place/polygonId/${polygonId}`;

    return this.query
      .getObservable<DestinationInformationResponse>(
        url,
        this.contentApiHeaders()
      )
      .pipe(
        camelCased({ deep: true }),
        map((response) => ({
          ...response,
          imageNames: response.imageNames.map((imageName) =>
            getImageUrl({ imageName, imageService: 'sembo' })
          ),
        }))
      );
  }

  private searchPortStaticData(
    options: AutoSuggestionOptions
  ): Observable<ContentApiPortStaticData[]> {
    const queryString: QueryString = {
      Types: [options.type],
      Size: [`${options.size}`],
    };

    if ('searchTerm' in options) {
      queryString.Term = [options.searchTerm];
    }

    if ('position' in options) {
      queryString.Latitude = [`${options.position.latitude}`];
      queryString.Longitude = [`${options.position.longitude}`];
    }

    if ('iata' in options) {
      queryString.IataCode = [options.iata];
    }

    if ('harbour' in options) {
      queryString.Harbour = [options.harbour];
    }

    if ('termFrom' in options && options.termFrom) {
      queryString.TermFrom = [options.termFrom];
    }
    if ('termTo' in options && options.termTo) {
      queryString.TermTo = [options.termTo];
    }
    if ('latitudeFrom' in options && options.latitudeFrom) {
      queryString.LatitudeFrom = [options.latitudeFrom.toString()];
    }
    if ('longitudeFrom' in options && options.longitudeFrom) {
      queryString.LongitudeFrom = [options.longitudeFrom.toString()];
    }
    if ('latitudeTo' in options && options.latitudeTo) {
      queryString.LatitudeTo = [options.latitudeTo.toString()];
    }
    if ('longitudeTo' in options && options.longitudeTo) {
      queryString.LongitudeTo = [options.longitudeTo.toString()];
    }

    return this.query
      .getObservable<ContentApiPortStaticData[]>(
        this.getAutosuggestionUrl(queryString),
        this.contentApiHeaders()
      )
      .pipe(camelCased({ deep: true }));
  }

  private getAutosuggestionUrl(query: QueryString): string {
    return `Autosuggestion?${renderQuery(query)}`;
  }

  private contentApiHeaders(options?: {
    forceDefaultMarket?: boolean;
  }): HeadersInit {
    return {
      'X-Sembo-Host': getDomainMarketOrigin(options),
      'X-Sembo-App': 'planner',
    };
  }

  private mapToAirportStaticInfo(
    port: ContentApiPortStaticData
  ): AirportStaticInfo {
    const airport = port.airport;
    assertValue(airport, 'contentApi.ts > mapToAirportStaticInfo > airport');

    return {
      type: 'airport',
      label: airport.label,
      code: airport.code,
      city: airport.city ?? undefined,
      latitude: airport.latitude,
      longitude: airport.longitude,
      popularity: airport.popularity,
    };
  }

  private mapToSeaportStaticInfo(
    port: ContentApiPortStaticData
  ): SeaportStaticInfo {
    const harbour = port.harbour;
    assertValue(harbour, 'contentApi.ts > mapToSeaportStaticInfo > harbour');

    return {
      type: 'harbour',
      address: harbour.address,
      code: harbour.code,
      label: harbour.label,
      latitude: harbour.latitude,
      longitude: harbour.longitude,
    };
  }

  private mapToFerryRoutes(
    data: ContentApiPortStaticData
  ): ContentApiFerryRouteStaticData {
    assertValue(
      data.ferryRoute,
      'contentApi.ts > mapToFerryRoutes > ferryRoute'
    );
    return data.ferryRoute;
  }

  private getTopPopularAirports(
    data: ContentApiPortStaticData[]
  ): ContentApiPortStaticData[] {
    const POSITION_SUGGESTION_COUNT = 10;

    const topPopularAirports = [...data]
      .sort(popularityAirportComparer)
      .splice(0, POSITION_SUGGESTION_COUNT);

    return data.filter(
      (airport) =>
        !!topPopularAirports.find(
          (popularAirport) =>
            popularAirport.airport?.code === airport.airport?.code
        )
    );
  }

  private mapToAutoCompleteSuggestion(
    data: ContentApiPortStaticData[],
    category: string
  ): FlightAutoCompleteSuggestion[] {
    return data.map((portStaticData) => {
      const airport = portStaticData.airport;
      assertValue(
        airport,
        'contentApi.ts > mapToAutoCompleteSuggestion > airport'
      );

      return {
        iata: airport.code,
        categoryType: CategoryType.Airport,
        label: airport.label,
        tooltip: airport.city ?? undefined,
        category,
      };
    });
  }
}
